import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import {Check, Loader2, Mail} from 'lucide-react'
import { useState } from 'react'
import AuthModal from './AuthModal'

interface EmailEstimateButtonProps {
  calculatorType: 'tattoo' | 'laser'
  summary: string
  inputs: Record<string, unknown>
  results: Record<string, unknown>
  className?: string
}

function EmailEstimateButton({ calculatorType, summary, inputs, results, className }: EmailEstimateButtonProps) {
  const { user, isAuthenticated } = useAuth()
  const [authOpen, setAuthOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    if (!isAuthenticated || !user?.userId) {
      setAuthOpen(true)
      return
    }
    setSending(true)
    setError(null)
    try {
      await lumi.functions.invoke('sendCostBreakdown', {
        method: 'POST',
        body: { calculatorType, summary, inputs, results },
      })
      setSent(true)
      setTimeout(() => setSent(false), 3000)
    }
    catch (err: any) {
      setError(err?.data?.error || err?.message || 'Could not send email. Please try again.')
    }
    finally {
      setSending(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleSend}
        disabled={sending}
        className={
          className
          || 'inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60'
        }
      >
        {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {!sending && sent && <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" />}
        {!sending && !sent && <Mail className="h-4 w-4" aria-hidden="true" />}
        {sent ? 'Sent to Your Inbox' : 'Email My Estimate'}
      </button>
      {error && (
        <p className="mt-2 text-xs text-rose-400" role="alert">{error}</p>
      )}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  )
}

export default EmailEstimateButton
